import { SUPPORTED_LANGUAGES } from './supportedLanguages';

/**
 * Resolve a possibly-multilingual branding value to a string.
 *
 * Accepts either:
 *   - a plain string (legacy / single-language)
 *   - an object keyed by language code: { en: 'Hi', tr: 'Merhaba' }
 *
 * Resolution order: requested lang → fallback lang → first non-empty value → ''.
 */
export function getLocalized(value, lang, fallbackLang = 'en') {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  if (lang && typeof value[lang] === 'string' && value[lang].trim()) return value[lang];
  if (fallbackLang && typeof value[fallbackLang] === 'string' && value[fallbackLang].trim()) {
    return value[fallbackLang];
  }
  for (const code of SUPPORTED_LANGUAGES) {
    if (typeof value[code] === 'string' && value[code].trim()) return value[code];
  }
  // last resort: any string value on the object
  for (const v of Object.values(value)) {
    if (typeof v === 'string' && v.trim()) return v;
  }
  return '';
}

/**
 * Every language variant of a localized value, lower-cased and trimmed.
 *
 * For matching user/AI-supplied text against a definition the user may have
 * named differently per language ("Turkish Lira" vs "Türk Lirası") — resolving
 * to the active language only would miss the other spellings.
 */
export function localizedNames(value) {
  if (value == null) return [];
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s ? [s] : [];
  }
  if (typeof value !== 'object') return [];
  return Object.values(value)
    .filter((v) => typeof v === 'string')
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Normalise editor state into the storage shape.
 *
 *   - Empty → ''
 *   - Only the primary language is filled → plain string (the primary value).
 *     This keeps the back-compat "plain string == organization default" rule.
 *   - Anything else (multiple langs, or a single non-primary lang) → object.
 *
 * IMPORTANT: storing a plain string for a single *non-primary* language is
 * unsafe because we can't tell on read which language it belongs to — the
 * value would silently re-hydrate under whatever the current primary language
 * is. Always emit an object in that case.
 */
export function normalizeMultilang(map, primaryLang = 'en') {
  if (!map || typeof map !== 'object') return '';
  const filled = Object.entries(map)
    .filter(([, v]) => typeof v === 'string' && v.trim().length > 0);
  if (filled.length === 0) return '';
  if (filled.length === 1 && filled[0][0] === primaryLang) return filled[0][1];
  return Object.fromEntries(filled);
}

/**
 * Convert a stored value (string or object) into an editor-friendly map keyed
 * by every supported language. The detected single-language string is placed
 * under `primaryLang` so the user can see it under the active language tab.
 */
export function toMultilangMap(value, primaryLang = 'en') {
  const map = Object.fromEntries(SUPPORTED_LANGUAGES.map((c) => [c, '']));
  if (value == null) return map;
  if (typeof value === 'string') {
    map[primaryLang] = value;
    return map;
  }
  if (typeof value === 'object') {
    for (const code of SUPPORTED_LANGUAGES) {
      if (typeof value[code] === 'string') map[code] = value[code];
    }
  }
  return map;
}
